import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { UIButton } from '../../features/ui-system';
import { Input } from '../../components/ui/input';
import AdminPackageMenuLayout from './components/AdminPackageMenuLayout';
import { API, getHeaders, toNumber } from './utils/packageManagementApi';

const emptyForm = {
  id: '',
  name: '',
  product_code: '',
  router_id: '',
  owner_id: '',
  local_address: '',
  pool_name: '',
  pool_ip_start: '',
  pool_ip_end: '',
  speed_mode: 'simple',
  upload_rate: '',
  download_rate: '',
  upload_burst: '',
  download_burst: '',
  upload_threshold: '',
  download_threshold: '',
  upload_burst_time: '',
  download_burst_time: '',
  tax_enabled: 'yes',
  cost_price: 0,
  reseller_fee: 0,
  price: 0,
  active_days: 30,
  duration_minutes: 0,
  quota_mb: 0,
  limit_mode: 'time',
  burst_profile: '',
  notes: '',
};
const BURST_FORMAT_REGEX = /^\d+(?:\.\d+)?[kKmMgG]?\/\d+(?:\.\d+)?[kKmMgG]?\s+\d+(?:\.\d+)?[kKmMgG]?\/\d+(?:\.\d+)?[kKmMgG]?\s+\d+\/\d+\s+[1-8]$/;

const readMeta = (notes) => {
  try {
    const txt = String(notes || '');
    if (!txt.startsWith('__meta__:')) {
      return {
        owner_id: '',
        notes: txt,
        product_code: '',
        local_address: '',
        pool_name: '',
        pool_ip_start: '',
        pool_ip_end: '',
        speed_mode: 'simple',
        upload_rate: '',
        download_rate: '',
        upload_burst: '',
        download_burst: '',
        upload_threshold: '',
        download_threshold: '',
        upload_burst_time: '',
        download_burst_time: '',
        tax_enabled: 'yes',
      };
    }
    const raw = txt.replace('__meta__:', '');
    const parsed = JSON.parse(raw);
    return {
      owner_id: parsed.owner_id || '',
      notes: parsed.notes || '',
      product_code: parsed.product_code || '',
      local_address: parsed.local_address || '',
      pool_name: parsed.pool_name || '',
      pool_ip_start: parsed.pool_ip_start || '',
      pool_ip_end: parsed.pool_ip_end || '',
      speed_mode: parsed.speed_mode === 'advanced' ? 'advanced' : 'simple',
      upload_rate: parsed.upload_rate || '',
      download_rate: parsed.download_rate || '',
      upload_burst: parsed.upload_burst || '',
      download_burst: parsed.download_burst || '',
      upload_threshold: parsed.upload_threshold || '',
      download_threshold: parsed.download_threshold || '',
      upload_burst_time: parsed.upload_burst_time || '',
      download_burst_time: parsed.download_burst_time || '',
      tax_enabled: parsed.tax_enabled === 'no' ? 'no' : 'yes',
    };
  } catch {
    return {
      owner_id: '',
      notes: String(notes || ''),
      product_code: '',
      local_address: '',
      pool_name: '',
      pool_ip_start: '',
      pool_ip_end: '',
      speed_mode: 'simple',
      upload_rate: '',
      download_rate: '',
      upload_burst: '',
      download_burst: '',
      upload_threshold: '',
      download_threshold: '',
      upload_burst_time: '',
      download_burst_time: '',
      tax_enabled: 'yes',
    };
  }
};

const writeMeta = (meta) => JSON.stringify(meta || {});

const buildBurstProfileFromAdvancedFields = (form) => {
  const downBurst = String(form.download_burst || '').trim();
  const upBurst = String(form.upload_burst || '').trim();
  const downThreshold = String(form.download_threshold || '').trim();
  const upThreshold = String(form.upload_threshold || '').trim();
  const downTime = String(form.download_burst_time || '').trim();
  const upTime = String(form.upload_burst_time || '').trim();
  if (!downBurst || !upBurst || !downThreshold || !upThreshold || !downTime || !upTime) return '';
  return `${downBurst}/${upBurst} ${downThreshold}/${upThreshold} ${downTime}/${upTime} 8`;
};

const toMbps = (value, unit) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  const normalizedUnit = String(unit || '').trim().toLowerCase();
  if (normalizedUnit === 'kbps') return numeric / 1000;
  return numeric;
};

const formatRateValue = (valueMbps) => {
  const safe = Number(valueMbps || 0);
  if (!Number.isFinite(safe) || safe <= 0) return '0';
  if (safe >= 1) {
    const rounded = Math.round(safe * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}M` : `${rounded.toFixed(1).replace(/\.0$/, '')}M`;
  }
  const kbps = Math.max(64, Math.round(safe * 1000));
  return `${kbps}k`;
};

const parseRateInputToMbps = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return 0;
  const numeric = Number(text.replace(',', '.').replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  if (text.includes('kb')) return numeric / 1000;
  if (text.endsWith('k')) return numeric / 1000;
  return numeric;
};

const normalizeBurstText = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const isValidBurstFormat = (value) => BURST_FORMAT_REGEX.test(normalizeBurstText(value));
const IP_V4_REGEX = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const isIpv4 = (value) => IP_V4_REGEX.test(String(value || '').trim());

const buildProvisioningReadiness = (profile = {}) => {
  const issues = [];
  if (!String(profile.router_id || '').trim()) issues.push('NAS/Router belum dipilih');
  if (!String(profile.local_address || '').trim()) issues.push('Alamat lokal kosong');
  if (!String(profile.pool_name || '').trim()) issues.push('Pool name kosong');
  if (!isIpv4(profile.pool_ip_start) || !isIpv4(profile.pool_ip_end)) issues.push('Format pool IP belum valid');
  if (!String(profile.upload_rate || '').trim() || !String(profile.download_rate || '').trim()) issues.push('Rate upload/download belum lengkap');
  if (String(profile.speed_mode || '').trim().toLowerCase() === 'advanced') {
    const burstCandidate = buildBurstProfileFromAdvancedFields(profile) || String(profile.burst_profile || '').trim();
    if (!burstCandidate || !isValidBurstFormat(burstCandidate)) {
      issues.push('Burst profile advanced belum valid');
    }
  }
  if (issues.length === 0) return { label: 'Ready', tone: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300', issues };
  if (issues.length <= 2) return { label: 'Warning', tone: 'border-amber-400/40 bg-amber-500/10 text-amber-300', issues };
  return { label: 'Critical', tone: 'border-red-400/40 bg-red-500/10 text-red-300', issues };
};

const mapButtonVariant = (variant) => {
  if (variant === 'outline' || variant === 'secondary') return 'subtle';
  if (variant === 'danger') return 'danger';
  if (variant === 'ghost') return 'ghost';
  return 'primary';
};

const Button = ({ variant = 'primary', className = '', ...props }) => (
  <UIButton variant={mapButtonVariant(variant)} className={className} {...props} />
);

const recommendBurstFromRates = (uploadBase, downloadBase, mode = 'balanced') => {
  if (!(uploadBase > 0) || !(downloadBase > 0)) return null;

  const burstMode = String(mode || 'balanced').trim().toLowerCase();
  const modeConfig = {
    conservative: { burstFactor: 1.25, thresholdFactor: 0.9, timeScale: 0.85, label: 'Conservative' },
    balanced: { burstFactor: 1.5, thresholdFactor: 0.8, timeScale: 1, label: 'Balanced' },
    aggressive: { burstFactor: 2.0, thresholdFactor: 0.7, timeScale: 1.25, label: 'Aggressive' },
  };
  const selected = modeConfig[burstMode] || modeConfig.balanced;

  const burstUp = uploadBase * selected.burstFactor;
  const burstDown = downloadBase * selected.burstFactor;
  const thresholdUp = uploadBase * selected.thresholdFactor;
  const thresholdDown = downloadBase * selected.thresholdFactor;
  const maxBase = Math.max(uploadBase, downloadBase);
  const baseBurstTime = maxBase <= 5 ? 20 : maxBase <= 10 ? 24 : maxBase <= 20 ? 32 : maxBase <= 50 ? 40 : 50;
  const burstTime = Math.max(10, Math.round(baseBurstTime * selected.timeScale));
  const priority = 8;

  return {
    text: `${formatRateValue(burstDown)}/${formatRateValue(burstUp)} ${formatRateValue(thresholdDown)}/${formatRateValue(thresholdUp)} ${burstTime}/${burstTime} ${priority}`,
    explanation: `Mode ${selected.label}. Dasar: max down ${downloadBase.toFixed(2)} Mbps, max up ${uploadBase.toFixed(2)} Mbps.`,
    derivedSpeedMbps: Math.max(1, Math.round(downloadBase)),
  };
};

const parseBurstRecommendation = (value) => {
  const raw = normalizeBurstText(value);
  const parts = raw.split(' ');
  if (parts.length < 4) return null;
  const burstParts = String(parts[0] || '').split('/');
  const thresholdParts = String(parts[1] || '').split('/');
  const timeParts = String(parts[2] || '').split('/');
  if (burstParts.length < 2 || thresholdParts.length < 2 || timeParts.length < 2) return null;
  return {
    download_burst: burstParts[0],
    upload_burst: burstParts[1],
    download_threshold: thresholdParts[0],
    upload_threshold: thresholdParts[1],
    download_burst_time: timeParts[0],
    upload_burst_time: timeParts[1],
    burst_profile: raw,
  };
};

export default function AdminPppProfileManagement() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nasOptions, setNasOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [burstMode, setBurstMode] = useState('balanced');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingRadiusId, setTestingRadiusId] = useState('');
  const [applyingRouterId, setApplyingRouterId] = useState('');
  const [search, setSearch] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculator, setCalculator] = useState({
    download_max: 10,
    download_unit: 'Mbps',
    upload_max: 10,
    upload_unit: 'Mbps',
    mode: 'balanced',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [res, nasRes, customerRes] = await Promise.all([
        axios.get(`${API}/service-profiles`, { headers: getHeaders() }),
        axios.get(`${API}/voucher/nas`, { headers: getHeaders(), params: { limit: 500 } }),
        axios.get(`${API}/customers`, { headers: getHeaders() }),
      ]);
      const nasItems = Array.isArray(nasRes?.data?.items) ? nasRes.data.items : [];
      const customerItems = Array.isArray(customerRes?.data) ? customerRes.data : [];
      const all = Array.isArray(res.data?.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []);
      const mapped = all
        .filter((x) => String(x.service_type || '').toLowerCase() === 'pppoe')
        .map((x) => {
          const meta = readMeta(x.notes);
          return {
            ...x,
            owner_id: meta.owner_id,
            notes_plain: meta.notes,
            product_code: meta.product_code || String(x.product_code || '').trim(),
            router_id: String(x.router_id || '').trim(),
            local_address: meta.local_address || String(x.local_address || '').trim(),
            pool_name: meta.pool_name || String(x.pool_name || '').trim(),
            pool_ip_start: meta.pool_ip_start || String(x.pool_ip_start || '').trim(),
            pool_ip_end: meta.pool_ip_end || String(x.pool_ip_end || '').trim(),
            speed_mode: meta.speed_mode || String(x.speed_mode || 'simple').trim().toLowerCase(),
            upload_rate: meta.upload_rate || String(x.upload_rate || '').trim(),
            download_rate: meta.download_rate || String(x.download_rate || '').trim(),
            upload_burst: meta.upload_burst || String(x.upload_burst || '').trim(),
            download_burst: meta.download_burst || String(x.download_burst || '').trim(),
            upload_threshold: meta.upload_threshold || String(x.upload_threshold || '').trim(),
            download_threshold: meta.download_threshold || String(x.download_threshold || '').trim(),
            upload_burst_time: meta.upload_burst_time || String(x.upload_burst_time || '').trim(),
            download_burst_time: meta.download_burst_time || String(x.download_burst_time || '').trim(),
            tax_enabled: meta.tax_enabled || (String(x.tax_enabled || '').trim().toLowerCase() === 'no' ? 'no' : 'yes'),
          };
        });
      setItems(mapped);
      setNasOptions(nasItems);
      setCustomers(customerItems);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat Paket Internet');
      setItems([]);
      setNasOptions([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const key = search.trim().toLowerCase();
    if (!key) return items;
    return items.filter((it) => `${it.name || ''} ${it.product_code || ''}`.toLowerCase().includes(key));
  }, [items, search]);

  const usageByPackageId = useMemo(() => {
    const mapped = {};
    (customers || []).forEach((customer) => {
      const packageId = String(customer?.package || '').trim();
      if (!packageId) return;
      mapped[packageId] = (mapped[packageId] || 0) + 1;
    });
    return mapped;
  }, [customers]);

  const packageRows = useMemo(() => {
    return filtered.map((item) => {
      const packageId = String(item?.id || '').trim();
      const customersCount = usageByPackageId[packageId] || 0;
      const price = Number(item?.price || 0);
      const monthlyRevenue = customersCount * price;
      const readiness = buildProvisioningReadiness(item);
      return {
        ...item,
        customers_count: customersCount,
        monthly_revenue: monthlyRevenue,
        readiness,
      };
    });
  }, [filtered, usageByPackageId]);

  const totalPackageRevenue = useMemo(
    () => packageRows.reduce((sum, item) => sum + Number(item?.monthly_revenue || 0), 0),
    [packageRows],
  );

  const nasName = (routerId) => nasOptions.find((item) => String(item.router_id || item.id || '').trim() === String(routerId || '').trim())?.name || '-';
  const uploadMbps = useMemo(() => parseRateInputToMbps(form.upload_rate), [form.upload_rate]);
  const downloadMbps = useMemo(() => parseRateInputToMbps(form.download_rate), [form.download_rate]);
  const burstRecommendation = useMemo(
    () => recommendBurstFromRates(uploadMbps, downloadMbps, burstMode),
    [uploadMbps, downloadMbps, burstMode],
  );
  const rateLimitPreview = useMemo(() => {
    const down = downloadMbps;
    const up = uploadMbps;
    if (!(down > 0) || !(up > 0)) return '-';
    const base = `${formatRateValue(down)}/${formatRateValue(up)}`;
    const burst = normalizeBurstText(form.burst_profile || '');
    return burst ? `${base} ${burst}` : base;
  }, [downloadMbps, uploadMbps, form.burst_profile]);
  const formReadiness = useMemo(() => buildProvisioningReadiness(form), [form]);
  const formPoolValid = useMemo(() => isIpv4(form.pool_ip_start) && isIpv4(form.pool_ip_end), [form.pool_ip_start, form.pool_ip_end]);
  const calculatorRecommendation = useMemo(() => {
    const calcUp = toMbps(calculator.upload_max, calculator.upload_unit);
    const calcDown = toMbps(calculator.download_max, calculator.download_unit);
    return recommendBurstFromRates(calcUp, calcDown, calculator.mode);
  }, [calculator]);

  const clonePackage = (item) => {
    setBurstMode('balanced');
    setForm({
      ...emptyForm,
      ...item,
      id: '',
      name: `${item?.name || 'Paket'} (Copy)`,
      product_code: '',
      router_id: String(item?.router_id || '').trim(),
      burst_profile: item?.burst_profile || item?.burst_config || '',
      notes: item?.notes_plain || '',
    });
    toast.success('Template paket berhasil disalin. Lengkapi kode produk baru sebelum simpan.');
  };

  useEffect(() => {
    if (form.speed_mode !== 'advanced') return;
    const parsed = parseBurstRecommendation(burstRecommendation?.text || '');
    if (!parsed) return;
    setForm((prev) => {
      const next = { ...prev, ...parsed };
      const unchanged = (
        String(prev.upload_burst || '') === String(next.upload_burst || '')
        && String(prev.download_burst || '') === String(next.download_burst || '')
        && String(prev.upload_threshold || '') === String(next.upload_threshold || '')
        && String(prev.download_threshold || '') === String(next.download_threshold || '')
        && String(prev.upload_burst_time || '') === String(next.upload_burst_time || '')
        && String(prev.download_burst_time || '') === String(next.download_burst_time || '')
        && String(prev.burst_profile || '') === String(next.burst_profile || '')
      );
      return unchanged ? prev : next;
    });
  }, [form.speed_mode, burstRecommendation?.text]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Nama paket internet wajib diisi');
      return;
    }
    if (!String(form.product_code || '').trim()) {
      toast.error('Kode produk wajib diisi');
      return;
    }
    if (!String(form.price || '').trim()) {
      toast.error('Harga produk wajib diisi');
      return;
    }
    if (!String(form.local_address || '').trim()) {
      toast.error('Alamat lokal wajib diisi');
      return;
    }
    if (!String(form.pool_name || '').trim()) {
      toast.error('Pool name wajib diisi');
      return;
    }
    if (!String(form.pool_ip_start || '').trim() || !String(form.pool_ip_end || '').trim()) {
      toast.error('Pool IP awal/akhir wajib diisi');
      return;
    }
    if (!String(form.upload_rate || '').trim() || !String(form.download_rate || '').trim()) {
      toast.error('Upload dan Download wajib diisi');
      return;
    }
    if (!String(form.router_id || '').trim()) {
      toast.error('NAS/Router wajib dipilih');
      return;
    }
    const burstCandidate = form.speed_mode === 'advanced'
      ? buildBurstProfileFromAdvancedFields(form)
      : String(form.burst_profile || '');
    const burstValue = normalizeBurstText(burstCandidate || '');
    if (burstValue && !isValidBurstFormat(burstValue)) {
      toast.error('Format burst tidak valid. Gunakan format: 15M/15M 8M/8M 32/32 8');
      return;
    }

    const parsedDown = Number(String(form.download_rate || '').replace(/[^\d.]/g, ''));
    const speedFromForm = Number.isFinite(parsedDown) && parsedDown > 0 ? parsedDown : 0;

    const metaPayload = {
      owner_id: form.owner_id || '',
      notes: form.notes || '',
      product_code: String(form.product_code || '').trim(),
      local_address: String(form.local_address || '').trim(),
      pool_name: String(form.pool_name || '').trim(),
      pool_ip_start: String(form.pool_ip_start || '').trim(),
      pool_ip_end: String(form.pool_ip_end || '').trim(),
      speed_mode: form.speed_mode === 'advanced' ? 'advanced' : 'simple',
      upload_rate: String(form.upload_rate || '').trim(),
      download_rate: String(form.download_rate || '').trim(),
      upload_burst: String(form.upload_burst || '').trim(),
      download_burst: String(form.download_burst || '').trim(),
      upload_threshold: String(form.upload_threshold || '').trim(),
      download_threshold: String(form.download_threshold || '').trim(),
      upload_burst_time: String(form.upload_burst_time || '').trim(),
      download_burst_time: String(form.download_burst_time || '').trim(),
      tax_enabled: form.tax_enabled === 'no' ? 'no' : 'yes',
    };

    const payload = {
      name: form.name.trim(),
      service_type: 'pppoe',
      product_code: String(form.product_code || '').trim().toUpperCase(),
      router_id: String(form.router_id || '').trim(),
      local_address: String(form.local_address || '').trim(),
      pool_name: String(form.pool_name || '').trim(),
      pool_ip_start: String(form.pool_ip_start || '').trim(),
      pool_ip_end: String(form.pool_ip_end || '').trim(),
      speed_mode: form.speed_mode === 'advanced' ? 'advanced' : 'simple',
      upload_rate: String(form.upload_rate || '').trim(),
      download_rate: String(form.download_rate || '').trim(),
      upload_burst: String(form.upload_burst || '').trim() || null,
      download_burst: String(form.download_burst || '').trim() || null,
      upload_threshold: String(form.upload_threshold || '').trim() || null,
      download_threshold: String(form.download_threshold || '').trim() || null,
      upload_burst_time: String(form.upload_burst_time || '').trim() || null,
      download_burst_time: String(form.download_burst_time || '').trim() || null,
      tax_enabled: form.tax_enabled === 'no' ? 'no' : 'yes',
      cost_price: toNumber(form.cost_price),
      reseller_fee: toNumber(form.reseller_fee),
      price: toNumber(form.price, toNumber(form.cost_price) + toNumber(form.reseller_fee)),
      speed_mbps: Math.round(speedFromForm || burstRecommendation?.derivedSpeedMbps || 0),
      active_days: toNumber(form.active_days),
      duration_minutes: toNumber(form.duration_minutes),
      quota_mb: toNumber(form.quota_mb),
      limit_mode: form.limit_mode || 'time',
      notes: `__meta__:${writeMeta(metaPayload)}`,
      burst_profile: burstValue || null,
    };

    setSaving(true);
    try {
      if (form.id) {
        const response = await axios.put(`${API}/service-profiles/${form.id}`, payload, { headers: getHeaders() });
        toast.success(response?.data?.message || 'Paket Internet diperbarui');
      } else {
        const response = await axios.post(`${API}/service-profiles`, payload, { headers: getHeaders() });
        toast.success(response?.data?.message || 'Paket Internet ditambahkan');
      }
      setForm(emptyForm);
      setBurstMode('balanced');
      setShowCalculator(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan Paket Internet');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Hapus Paket Internet ini?')) return;
    try {
      await axios.delete(`${API}/service-profiles/${id}`, { headers: getHeaders() });
      toast.success('Paket Internet dihapus');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus Paket Internet');
    }
  };

  const onTestFreeradius = async (id) => {
    setTestingRadiusId(id);
    try {
      const response = await axios.get(`${API}/service-profiles/${id}/freeradius-test`, { headers: getHeaders() });
      const payload = response?.data || {};
      const preview = payload?.radius_preview || {};
      if (payload?.ok) {
        toast.success(`Tes Radius OK • Rate: ${preview.rate_limit || '-'} • Pool: ${preview.framed_pool || '-'}`);
      } else {
        const issues = Array.isArray(payload?.issues) ? payload.issues.join(', ') : 'Konfigurasi belum lengkap';
        toast.error(`Tes Radius gagal: ${issues}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Tes FreeRADIUS gagal');
    } finally {
      setTestingRadiusId('');
    }
  };

  const onRouterApplyScript = async (id) => {
    setApplyingRouterId(id);
    try {
      const response = await axios.get(`${API}/service-profiles/${id}/router-apply-script`, { headers: getHeaders() });
      const payload = response?.data || {};
      const apply = payload?.router_apply || {};
      const commands = Array.isArray(apply?.commands) ? apply.commands : [];
      if (!commands.length) {
        toast.error('Script router apply belum tersedia untuk paket ini');
        return;
      }
      const scriptText = commands.join('\n');
      try {
        await navigator.clipboard.writeText(scriptText);
        toast.success(`Script router apply (${apply?.routerName || '-'}) berhasil disalin`);
      } catch {
        toast.error('Gagal menyalin script. Salin manual dari console browser response.');
      }

      const markApplied = window.confirm('Script sudah disalin. Setelah dijalankan di terminal router, klik OK untuk tandai Applied.');
      if (markApplied) {
        const markResponse = await axios.post(
          `${API}/service-profiles/${id}/router-apply-mark`,
          { applied: true, note: `Applied manual oleh operator ${new Date().toISOString()}` },
          { headers: getHeaders() },
        );
        toast.success(markResponse?.data?.message || 'Status router apply diperbarui');
      }
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal mengambil script router apply');
    } finally {
      setApplyingRouterId('');
    }
  };

  return (
    <AdminPackageMenuLayout
      eyebrow="Billing"
      title="Paket Internet (PPPoE)"
      subtitle="Definisikan produk internet, template provisioning FreeRADIUS, dan dampak paket ke pelanggan."
      activePath="/dadu/manajemen/ppp-profile"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-white/10 bg-[#121212] p-3">
            <p className="text-[11px] text-gray-400">Total Paket</p>
            <p className="text-xl text-white font-semibold">{items.length}</p>
          </div>
          <div className="rounded-xl border border-emerald-400/20 bg-[#121212] p-3">
            <p className="text-[11px] text-gray-400">Paket Aktif</p>
            <p className="text-xl text-emerald-300 font-semibold">{items.filter((it) => String(it?.is_active ?? it?.active ?? true) !== 'false').length}</p>
          </div>
          <div className="rounded-xl border border-cyan-400/20 bg-[#121212] p-3">
            <p className="text-[11px] text-gray-400">Pelanggan Terlayani</p>
            <p className="text-xl text-cyan-300 font-semibold">{customers.length}</p>
          </div>
          <div className="rounded-xl border border-amber-400/20 bg-[#121212] p-3">
            <p className="text-[11px] text-gray-400">Estimasi Revenue/Bulan</p>
            <p className="text-xl text-amber-300 font-semibold">Rp {totalPackageRevenue.toLocaleString('id-ID')}</p>
          </div>
        </div>

        <div className="ktc-form-shell rounded-2xl border border-white/10 bg-[#121212] p-5 shadow-sm">
          <h2 className="text-white font-semibold mb-4">{form.id ? 'Edit Paket Internet' : 'Tambah Paket Internet'}</h2>
          <form className="space-y-3" onSubmit={onSubmit}>
            <div className="rounded border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-[11px] text-cyan-200">
              Layer A: Product Definition, Layer B: Provisioning Template, Layer C: Usage/Impact.
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0A0A0A]/60 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Layer A — Product Definition</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${formReadiness.tone}`}>{formReadiness.label}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Nama Paket Internet *</p>
                  <Input placeholder="Nama Paket Internet" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                  <p className="text-[11px] text-gray-400 mt-1">Contoh: Paket Home 10 Mbps, Paket Home 20 Mbps.</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Kode Produk *</p>
                  <Input
                    placeholder="Contoh PKT-10-MBPS"
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400 font-mono"
                    value={form.product_code}
                    onChange={(e) => setForm((p) => ({ ...p, product_code: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Harga Produk (Rp) *</p>
                  <Input type="number" placeholder="Rp 100.000" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Pajak *</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`px-3 py-2 rounded border text-left ${form.tax_enabled === 'yes' ? 'border-cyan-400 bg-cyan-500/10 text-cyan-200' : 'border-white/10 text-gray-300'}`}
                      onClick={() => setForm((p) => ({ ...p, tax_enabled: 'yes' }))}
                    >
                      Ya
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-2 rounded border text-left ${form.tax_enabled === 'no' ? 'border-cyan-400 bg-cyan-500/10 text-cyan-200' : 'border-white/10 text-gray-300'}`}
                      onClick={() => setForm((p) => ({ ...p, tax_enabled: 'no' }))}
                    >
                      Tidak
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0A0A0A]/60 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-white">Layer B — Provisioning Template</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">NAS / Router *</p>
                  <select
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded text-white"
                    value={form.router_id || 'none'}
                    onChange={(e) => {
                      const nextRouterId = e.target.value === 'none' ? '' : e.target.value;
                      setForm((prev) => ({ ...prev, router_id: nextRouterId }));
                    }}
                  >
                    <option value="none">- Pilih NAS / Router -</option>
                    {nasOptions.map((nas) => {
                      const routerId = String(nas.router_id || nas.id || '').trim();
                      if (!routerId) return null;
                      return <option key={routerId} value={routerId}>{nas.name || routerId}</option>;
                    })}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">Router per site. Jika NAS belum ada, tambahkan dulu di Master Site/NAS.</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Alamat Lokal *</p>
                  <Input
                    placeholder="192.168.x.1"
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                    value={form.local_address}
                    onChange={(e) => setForm((p) => ({ ...p, local_address: e.target.value }))}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Pool Name *</p>
                  <Input
                    placeholder="pool-name"
                    className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                    value={form.pool_name}
                    onChange={(e) => setForm((p) => ({ ...p, pool_name: e.target.value }))}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-300 mb-1">Pool IP *</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="IP awal"
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                      value={form.pool_ip_start}
                      onChange={(e) => setForm((p) => ({ ...p, pool_ip_start: e.target.value }))}
                    />
                    <Input
                      placeholder="IP akhir"
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                      value={form.pool_ip_end}
                      onChange={(e) => setForm((p) => ({ ...p, pool_ip_end: e.target.value }))}
                    />
                  </div>
                  <p className={`text-[11px] mt-1 ${formPoolValid ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {formPoolValid ? 'Pool IP terlihat valid.' : 'Format pool IP perlu dicek lagi.'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-300">Speed Configuration *</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`px-3 py-2 rounded border text-left ${form.speed_mode === 'simple' ? 'border-cyan-400 bg-cyan-500/10 text-cyan-200' : 'border-white/10 text-gray-300'}`}
                    onClick={() => setForm((p) => ({ ...p, speed_mode: 'simple' }))}
                  >
                    Simple
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-2 rounded border text-left ${form.speed_mode === 'advanced' ? 'border-cyan-400 bg-cyan-500/10 text-cyan-200' : 'border-white/10 text-gray-300'}`}
                    onClick={() => setForm((p) => ({ ...p, speed_mode: 'advanced' }))}
                  >
                    Advanced
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs font-medium text-gray-300 mb-1">Upload *</p>
                    <Input
                      placeholder="5M"
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                      value={form.upload_rate}
                      onChange={(e) => setForm((p) => ({ ...p, upload_rate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-300 mb-1">Download *</p>
                    <Input
                      placeholder="5M"
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400"
                      value={form.download_rate}
                      onChange={(e) => setForm((p) => ({ ...p, download_rate: e.target.value }))}
                    />
                  </div>
                </div>
                {form.speed_mode === 'advanced' && (
                  <div className="space-y-2 rounded border border-white/10 bg-[#050505]/80 p-3">
                    <p className="text-[11px] text-cyan-200">Advanced shaping / burst</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Upload Burst" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.upload_burst} onChange={(e) => setForm((p) => ({ ...p, upload_burst: e.target.value }))} />
                      <Input placeholder="Download Burst" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.download_burst} onChange={(e) => setForm((p) => ({ ...p, download_burst: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Upload Threshold" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.upload_threshold} onChange={(e) => setForm((p) => ({ ...p, upload_threshold: e.target.value }))} />
                      <Input placeholder="Download Threshold" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.download_threshold} onChange={(e) => setForm((p) => ({ ...p, download_threshold: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Upload Burst Time" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.upload_burst_time} onChange={(e) => setForm((p) => ({ ...p, upload_burst_time: e.target.value }))} />
                      <Input placeholder="Download Burst Time" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.download_burst_time} onChange={(e) => setForm((p) => ({ ...p, download_burst_time: e.target.value }))} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 p-3 text-[11px] text-cyan-100 space-y-1">
              <p className="font-semibold">Provisioning Preview</p>
              <p>NAS/Router: <span className="font-medium">{nasName(form.router_id)}</span></p>
              <p>Pool: <span className="font-medium">{form.pool_name || '-'} ({form.pool_ip_start || '-'} - {form.pool_ip_end || '-'})</span></p>
              <p>Effective Speed: <span className="font-medium">{form.download_rate || '-'} down / {form.upload_rate || '-'} up</span></p>
              <p>Radius profile summary: <span className="font-medium">{rateLimitPreview}</span></p>
              {formReadiness.issues.length > 0 && (
                <p className="text-amber-200">Warning: {formReadiness.issues[0]}</p>
              )}
            </div>

            <div className="space-y-3 mt-1">
              <p className="text-xs font-medium text-gray-300">Catatan / Deskripsi singkat</p>
              <Input placeholder="Catatan" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="btn-primary text-white" disabled={saving}>{saving ? 'Menyimpan...' : (form.id ? 'Update Paket' : 'Tambah Paket')}</Button>
              {form.id && <Button type="button" variant="outline" className="btn-secondary" onClick={() => setForm(emptyForm)}>Batal</Button>}
            </div>
          </form>
        </div>

        <div className="ktc-table-card rounded-2xl border border-white/10 bg-[#121212] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h2 className="text-white font-semibold">Daftar Paket Internet</h2>
            <Input placeholder="Cari..." className="w-64 bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="ktc-table-shell overflow-x-auto">
            <table className="ktc-data-table w-full text-sm">
              <thead>
                <tr className="text-left text-gray-300 border-b border-white/10">
                  <th className="py-2">Nama Paket</th>
                  <th className="py-2">Kode</th>
                  <th className="py-2">Speed</th>
                  <th className="py-2">Harga</th>
                  <th className="py-2">Pelanggan</th>
                  <th className="py-2">Revenue</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">NAS</th>
                  <th className="py-2">Readiness</th>
                  <th className="py-2">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td className="py-4 text-gray-400" colSpan={10}>Memuat...</td></tr>}
                {!loading && packageRows.length === 0 && <tr><td className="py-4 text-gray-400" colSpan={10}>Belum ada data</td></tr>}
                {!loading && packageRows.map((it) => (
                  <tr key={it.id} className="border-b border-white/10">
                    <td className="py-2 text-white">{it.name}</td>
                    <td className="py-2 text-gray-300 font-mono">{it.product_code || '-'}</td>
                    <td className="py-2 text-gray-300">{it.download_rate || '-'} / {it.upload_rate || '-'}</td>
                    <td className="py-2 text-gray-300">Rp {Number(it.price || 0).toLocaleString('id-ID')}</td>
                    <td className="py-2 text-gray-300">{it.customers_count}</td>
                    <td className="py-2 text-gray-300">Rp {Number(it.monthly_revenue || 0).toLocaleString('id-ID')}</td>
                    <td className="py-2 text-gray-300">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] border ${String(it?.is_active ?? it?.active ?? true) !== 'false' ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300' : 'border-slate-400/40 bg-slate-500/10 text-slate-300'}`}>
                        {String(it?.is_active ?? it?.active ?? true) !== 'false' ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="py-2 text-gray-300">{nasName(it.router_id)}</td>
                    <td className="py-2 text-gray-300">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] border ${it.readiness.tone}`}>
                        {it.readiness.label}
                      </span>
                      <div className="mt-1 text-[10px] text-slate-400">
                        Sync: {String(it.sync_status || 'pending_router_apply')}
                      </div>
                    </td>
                    <td className="py-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => onTestFreeradius(it.id)}
                        disabled={testingRadiusId === it.id}
                      >
                        {testingRadiusId === it.id ? 'Tes...' : 'Tes Radius'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => onRouterApplyScript(it.id)}
                        disabled={applyingRouterId === it.id}
                      >
                        {applyingRouterId === it.id ? 'Applying...' : 'Router Apply'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => clonePackage(it)}
                      >
                        Clone
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => {
                          setBurstMode('balanced');
                          setForm({
                            ...emptyForm,
                            ...it,
                            router_id: String(it.router_id || '').trim(),
                            burst_profile: it.burst_profile || it.burst_config || '',
                            notes: it.notes_plain || '',
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => navigate(`/dadu/pelanggan?package=${encodeURIComponent(String(it.id || ''))}`)}
                      >
                        Pelanggan
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => onDelete(it.id)}>Hapus</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminPackageMenuLayout>
  );
}


